import * as Sentry from '@sentry/react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import Constants from 'expo-constants';
import posthog from 'posthog-js';
import { apiService } from '../services/apiService.js';
import logger from './logger.js';
import supabase from './supabaseClient.js';

// Get app frontend base URL from config
const APP_FRONTEND_BASE_URL =
  Constants.expoConfig?.extra?.appFrontendBaseUrl || 'https://doocars.com';

// The handleAuthSuccess function is now REMOVED. Its logic is no longer needed here.

// Pre-signup deliverability gate. Returns a user-facing error string if the
// address should be blocked (typo / fake / disposable), or null to proceed.
// FAILSAFE: any failure of the validator itself returns null so signup is never
// blocked by it (the edge function also logs the failure to Sentry).
const runDeliverabilityCheck = async (email) => {
  try {
    const verdict = await apiService.invokeEdgeFunction('validate-email', {
      body: { email },
      skipAuth: true,
    });
    if (verdict && verdict.allow === false) {
      try {
        posthog.capture?.('email_validation_blocked', {
          status: verdict.status || null,
          sub_status: verdict.sub_status || null,
          had_suggestion: !!verdict.did_you_mean,
        });
      } catch (_e) {
        // PostHog must never break signup.
      }
      const suggestion = verdict.did_you_mean ? ` Did you mean ${verdict.did_you_mean}?` : '';
      // Message intentionally contains "valid email address" so every signup screen
      // routes errors with that phrase to the email field (see isEmailError checks).
      return `That email address doesn't look able to receive mail.${suggestion} Please enter a valid email address.`;
    }
    return null;
  } catch (e) {
    // Validator unreachable / errored, proceed with signup (fail open).
    logger.warn('[authService] Email deliverability check skipped:', e?.message);
    return null;
  }
};

// In-flight dedup. A double-submit (the button plus Enter/onSubmitEditing, or a
// re-render) fires the signup handler 2-3x in the same instant; without this each
// call would hit validate-email and burn a separate ZeroBounce credit (free tier
// is 100/mo). Concurrent checks for the same address share ONE edge invocation via
// the in-flight promise, and the entry clears as soon as it settles so a genuine
// later retry still re-validates. Keyed by the normalized email.
const _inflightDeliverability = new Map();

const checkEmailDeliverability = (email) => {
  const key = (email || '').trim().toLowerCase();
  // Nothing to dedup on an empty address; run the (cheap) check directly.
  if (!key) return runDeliverabilityCheck(email);
  const inflight = _inflightDeliverability.get(key);
  if (inflight) return inflight;
  const promise = runDeliverabilityCheck(email).finally(() => {
    _inflightDeliverability.delete(key);
  });
  _inflightDeliverability.set(key, promise);
  return promise;
};

const signUpWithEmail = async (email, password, onboardingAnswers, region, country_code, turnstileToken, intent) => {
  try {
    const deliverabilityError = await checkEmailDeliverability(email);
    if (deliverabilityError) {
      return { success: false, error: { message: deliverabilityError } };
    }

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        captchaToken: turnstileToken,
        data: {
          onboarding_answers: onboardingAnswers,
          onboarding_intent: intent || null,
          provider: 'email',
          region: region,
          country_code: country_code
        },
      },
    });

    if (error) throw error;

    // Supabase email enumeration protection: if the email is already registered,
    // signUp returns a user with an empty identities array and no error.
    // Detect this and return a clear error instead of a fake "needs confirmation".
    if (data.user && (!data.user.identities || data.user.identities.length === 0)) {
      return {
        success: false,
        error: { message: 'This email is already registered. Please try logging in.' },
      };
    }

    return { success: !!data.session };
  } catch (error) {
    logger.error('signUpWithEmail service error:', error.message);

    // Only capture unexpected errors to Sentry (not user validation or transient network errors)
    const isUserError =
      error.message.includes('User already registered') ||
      error.message.includes('Password should be at least') ||
      error.message.includes('Unable to validate email address');
    const isTransientNetworkError = error.name === 'AuthRetryableFetchError';

    if (!isUserError && !isTransientNetworkError) {
      Sentry.captureException(error, {
        tags: { operation: 'signUpWithEmail', service: 'auth' },
      });
    }

    // Differentiate user-facing errors
    let userMessage = 'An unexpected error occurred during sign up.';
    if (error.message.includes('User already registered')) {
      userMessage = 'This email is already registered. Please try logging in.';
    } else if (error.message.includes('Password should be at least')) {
      userMessage = 'Password is too weak. It should be at least 8 characters long.';
    } else if (error.message.includes('Unable to validate email address')) {
      userMessage = 'Please enter a valid email address.';
    }
    return { success: false, error: { message: userMessage } };
  }
};

const signUpWithGoogle = async (onboardingAnswers, region, country_code, intent) => {
  // This function now only initiates the OAuth flow.
  // The redirect and session handling are managed by the AuthProvider listener.
  try {
    // Check if there's a pending purchase to claim after OAuth
    // This is set during Flow B payment (anonymous user purchases before signup)
    const hasPendingPurchase =
      typeof localStorage !== 'undefined' && localStorage.getItem('purchase_session_id');

    // Determine redirect path:
    // 1. If pending purchase → auth-callback (to claim subscription)
    // 2. Else → onboarding (existing flow for auto-completion)
    let redirectPath;
    if (hasPendingPurchase) {
      redirectPath = '/auth-callback?claim=true';
      logger.log('[authService] Pending purchase detected, will redirect to auth-callback');
    } else {
      // Redirect to /onboarding after OAuth
      // The onboarding page will detect the authenticated user and redirect to dashboard
      redirectPath = '/onboarding';
    }

    // Persist PostHog distinct_id before OAuth redirect so identity merge works
    // even if cookies/localStorage are cleared during the redirect
    try {
      const distinctId = posthog.get_distinct_id?.();
      if (distinctId) {
        await AsyncStorage.setItem('posthog_pre_oauth_distinct_id', distinctId);
      }
      // Flush pending events before redirect — otherwise onboarding_completed
      // and other queued events may be lost when the page unloads
      posthog.flush?.();
    } catch (e) {
      logger.warn('[authService] Failed to persist PostHog state before OAuth:', e);
    }

    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        data: {
          onboarding_answers: onboardingAnswers,
          onboarding_intent: intent || null,
          region: region,
          country_code: country_code
        },
        redirectTo: `${APP_FRONTEND_BASE_URL}${redirectPath}`,
      },
    });
    if (error) throw error;
    return { success: true, redirectPath };
  } catch (error) {
    logger.error('signUpWithGoogle service error:', error.message);
    if (error.name !== 'AuthRetryableFetchError') {
      Sentry.captureException(error, {
        tags: { operation: 'signUpWithGoogle', service: 'auth' },
      });
    }
    return { success: false, error: { message: 'Failed to sign up with Google.' } };
  }
};

const loginWithEmail = async (email, password, turnstileToken) => {
  try {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
      options: { captchaToken: turnstileToken },
    });
    if (error) throw error;
    return { success: true, redirectPath: '/home' };
  } catch (error) {
    logger.error('loginWithEmail service error:', error.message);

    // Only capture unexpected errors to Sentry (not user validation or transient network errors)
    const isUserError =
      error.message.includes('Invalid login credentials') ||
      error.message.includes('Email not confirmed');
    const isTransientNetworkError = error.name === 'AuthRetryableFetchError';

    if (!isUserError && !isTransientNetworkError) {
      Sentry.captureException(error, {
        tags: { operation: 'loginWithEmail', service: 'auth' },
      });
    }

    let userMessage = 'An unexpected error occurred. Please try again.';
    if (error.message.includes('Invalid login credentials')) {
      userMessage = 'Invalid email or password. Please check your credentials.';
    }
    return { success: false, error: { message: userMessage } };
  }
};
const sendMagicLink = async (email, turnstileToken) => {
  const { error } = await supabase.auth.signInWithOtp({
    email,
    options: {
      emailRedirectTo: `${APP_FRONTEND_BASE_URL}/home`,
      shouldCreateUser: false,
      captchaToken: turnstileToken,
    },
  });
  if (error) {
    logger.error('Error sending magic link:', error.message);
  }
  return { success: true };
};

const resetPassword = async (email, turnstileToken) => {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${APP_FRONTEND_BASE_URL}/update-password`,
    captchaToken: turnstileToken,
  });
  if (error) throw error;
  return { success: true };
};

const signInWithOAuth = async (provider) => {
  try {
    // Persist PostHog distinct_id and flush before OAuth redirect
    try {
      const distinctId = posthog.get_distinct_id?.();
      if (distinctId) {
        await AsyncStorage.setItem('posthog_pre_oauth_distinct_id', distinctId);
      }
      posthog.flush?.();
    } catch (e) {
      logger.warn('[authService] Failed to persist PostHog state before OAuth:', e);
    }

    const { data, error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${APP_FRONTEND_BASE_URL}/auth-callback`,
      },
    });
    if (error) throw error;
    return { success: true, data, redirectPath: '/home' };
  } catch (error) {
    logger.error('signInWithOAuth service error:', error.message);
    if (error.name !== 'AuthRetryableFetchError') {
      Sentry.captureException(error, {
        tags: { operation: 'signInWithOAuth', service: 'auth' },
      });
    }
    return { success: false, error: { message: 'Failed to sign in with OAuth provider.' } };
  }
};

export const authService = {
  signUpWithEmail,
  signUpWithGoogle,
  loginWithEmail,
  resetPassword,
  signInWithOAuth,
  sendMagicLink,
};
